import React from "react";
import Rating from "@mui/material/Rating";
import Box from "@mui/material/Box";

// Assets
import { ReactComponent as Star } from "@/assets/icons/common/star.svg";
import { ReactComponent as EmptyStart } from "@/assets/icons/common/empty_star.svg";

interface StarRatingProps {
	value: number;
	maxStars?: number;
	precision?: number;
	readOnly?: boolean;
	onChange?: (event: React.ChangeEvent<{}>, value: number | null) => void;
	className?: string; // Add className prop
}

/**
 * StarRating Component
 *
 * This component displays a star rating using the Material-UI Rating component.
 *
 * Props:
 * - value (number): The number of filled stars to display.
 * - maxStars (number, optional): The maximum number of stars to display. Default is 5.
 * - precision (number, optional): The precision of the rating. Default is 0.1.
 * - readOnly (boolean, optional): Whether the rating is read-only. Default is true.
 * - onChange (function, optional): Callback function when the rating value changes.
 * - className (string, optional): Additional class name to apply to the Rating component.
 *
 * Example usage:
 * <StarRating value={3} maxStars={5} precision={0.1} readOnly={false} onChange={(event, newValue) => handleRatingChange(newValue)} className="custom-class" />
 */

export default function StarRating({
	value,
	maxStars = 5,
	precision = 0.1,
	readOnly = true,
	onChange,
	className,
}: Readonly<StarRatingProps>) {
	return (
		<Box component="fieldset" borderColor="transparent">
			<Rating
				icon={<Star />}
				emptyIcon={<EmptyStart />}
				readOnly={readOnly}
				max={maxStars}
				precision={precision}
				name="simple-controlled"
				value={value}
				onChange={onChange}
				className={className} // Apply the className prop
				sx={{
					"& .MuiRating-icon": {
						marginRight: 1.25,
						height: 17,
					},
				}}
			/>
		</Box>
	);
}
